import { FieldStats, POIStats } from "shared/api/apiModels";
import { unitArea } from "shared/helpers/unitHelpers";
// All SVGs imported below
import { ReactComponent as CountSVG } from "assets/kpi/count.svg";
import { ReactComponent as EarCountSVG } from "assets/kpi/ear-count.svg";
import { ReactComponent as CropHealthySVG } from "assets/kpi/crop-healthy.svg";
import { ReactComponent as CropUnhealthySVG } from "assets/kpi/crop-unhealthy.svg";
import { ReactComponent as CropUniformitySVG } from "assets/kpi/crop-uniformity.svg";
import { ReactComponent as FloweringSVG } from "assets/kpi/flowering.svg";
import { ReactComponent as FruitSVG } from "assets/kpi/fruit.svg";
import { ReactComponent as GaiSVG } from "assets/kpi/gai.svg";
import { ReactComponent as GrowthStageSVG } from "assets/kpi/growth-stage.svg";
import { ReactComponent as InsectSVG } from "assets/kpi/insect.svg";
import { ReactComponent as NdviSVG } from "assets/kpi/ndvi.svg";
// import { ReactComponent as NrecSVG } from "assets/kpi/nrec.svg";
import { ReactComponent as WeedsSVG } from "assets/kpi/weeds.svg";
import { AssertUniqueKeysArray } from "shared/helpers/helperModels";

/**
 * NOTES:
 *  - Growth stage AAB and BBCH are combined into the same stat. This needs checking/doing
 *  - Dynamic SVGs fill can be done via passing a function in `additionalClassNames`
 */

/**
 * NOTE: Unknown use for this yet, NREC info exists in WebClient but can't find examples or usage
  nitrogenRec: {
    name: "NREC",
    // displayName: "",
    description: "Estimate of the amount of Nitrogen required for the crop at that point, or for the field on average. This is a calculation based on crop GAI, born out of academic research. This calculation is crop-only and does not include weeds.\nNOTE: You will need to deduct soil N content, and any previous N applications from this number to give an estimated requirement for your next application.",
    // unit: "kg/Ha",
    svg: NrecSVG,
    // additionalClassNames: "SVGFillGreen",
  },
 */

/**
 * Base structure for a stat item
 */
type StatConfig = {
  /**
   * The name of the stat
   */
  name: string;
  /**
   * An optional display name for the stat, this is often an abbreviated or
   * more user-friendly version of the name.
   * @optional
   */
  displayName?: string;
  /**
   * A longer description of the stat.
   * This is used to provide more context to the user and explains in detail
   * what the stat measures and means.
   */
  description: string;
  /**
   * Units can be static or dynamic
   * REASON: Count per area could be in metric or imperial units
   * By having the ability for a function, the unit value can be calculated
   * upon render
   * This data is not currently sent by APIs
   * @optional
   */
  unit?: string | ((imperialUnits: boolean) => string);
  /**
   * The rendered values can be dynamic based on it's actual value.
   * e.g. Insect damage: "Not Detected" if "0", otherwise "Detected"
   *
   * If not provided, the value will be displayed "as is"
   * If provided and the value is `null`, hide the stat
   * @optional
   */
  value?: (value: string | number | null) => string | null;
  /**
   * Method 1: SVG source path when in public folder
   * This allows the the SVG to be loaded from the public folder,
   * but has issues where the svg was difficult to style using "react-svg"
   */
  /**
   * Method 2: SVG component
   * This allows a greater control over the SVG style and size
   */
  svg: React.FC<React.SVGProps<SVGSVGElement>>;
  /**
   * Icons can be static or dynamic
   * e.g. Insect damage: green if "0", otherwise red
   *
   * We can pass class names to the SVG, which in turn will change its colour
   *
   * If no value is provided, the SVG will render with the colour defined in
   * the SVG file
   * @optional
   */
  additionalClassNames?: string | ((value: string | number | null) => string);
};

export function defineFieldKPIs<
  T extends readonly (keyof typeof FIELD_STATS)[]
>(arr: AssertUniqueKeysArray<T, typeof FIELD_STATS>) {
  return arr;
}
export function definePoiKPIs<T extends readonly (keyof typeof POI_STATS)[]>(
  arr: AssertUniqueKeysArray<T, typeof POI_STATS>
) {
  return arr;
}

/**
 * This contains the configurations for each Field stat
 */
export const FIELD_STATS: Record<keyof FieldStats, StatConfig> = {
  count: {
    name: "Count",
    // displayName: "",
    description:
      "Counts are given as a simple number of plants counted in the image, and also per area value; it is an average based on the area covered by an image and the total plants in the image.",
    // unit: "",
    svg: CountSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  count_per_area: {
    name: "Count per Area",
    // displayName: "",
    description:
      "Counts are given as a simple number of plants counted in the image, and also per area value; it is an average based on the area covered by an image and the total plants in the image.",
    unit: unitArea,
    svg: CountSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  crop_uniformity: {
    name: "Crop Uniformity",
    // displayName: "",
    description:
      "Crop Uniformity\nA low value means there is a big difference in the amount of crop across the field. A high value means the crop is very similar throughout the field. 100% would mean the crop is the same everywhere.",
    unit: "%",
    svg: CropUniformitySVG,
    // additionalClassNames: "SVGFillGreen",
  },
  cropcover: {
    name: "Crop Cover",
    // displayName: "",
    description:
      "Crop Cover\nThis is the % of ground covered by healthy crop. 100% would mean that healthy crop covers the entire image.",
    unit: "%",
    svg: CropHealthySVG,
    // additionalClassNames: "SVGFillGreen",
  },
  desaturated: {
    name: "Desaturated",
    // displayName: "",
    description: "",
    unit: "%",
    value: (value) => (value === "true" ? "Desaturated" : null),
    svg: CropUnhealthySVG,
    additionalClassNames: "SVGFillRed",
  },
  ear_count: {
    name: "Ear Count",
    // displayName: "",
    description:
      "Ear counts are given as a simple number of ears counted in the image, and also per area value; it is an average based on the area covered by an image and the total ears in the image.",
    // unit: "",
    svg: EarCountSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  ear_count_per_area: {
    name: "Ear Count per Area",
    // displayName: "",
    description:
      "Ear counts are given as a simple number of ears counted in the image, and also per area value; it is an average based on the area covered by an image and the total ears in the image.",
    unit: unitArea,
    svg: EarCountSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  flowering: {
    name: "Flowering",
    // displayName: "",
    description:
      "Flowering Ratio (or Flower Fraction) is the percentage of the crop that is flowering vs not flowering. 100% would mean all of the crop is flowering. Note that the Crop Cover measurement includes green crop and flowering crop, the GAI measurement is green crop only.",
    unit: "%",
    svg: FloweringSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  gai: {
    name: "GAI",
    // displayName: "",
    description:
      "Crop GAI\nGreen Area Index. This stat gives a value estimate of leaf cover. It is based on healthy crop only and can be used as an estimate for crop biomass.",
    // unit: "",
    svg: GaiSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  growth_stage_aab: {
    name: "Growth Stage AAB",
    // displayName: "",
    description: "",
    // unit: "",
    svg: GrowthStageSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  growth_stage_bbch: {
    name: "Growth Stage BBCH",
    // displayName: "",
    description: "",
    // unit: "",
    svg: GrowthStageSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  health: {
    name: "Health",
    // displayName: "Health (0-100)",
    description:
      "Field NDVI\nThis stat is a measurement for plant health/vigor based on 10m square resolution satellite data. It gives a good general estimate for the amount of plant matter. Due to the resolution, it may not be accurate for specific points in the field and should be used only as a guide.",
    unit: "%",
    svg: NdviSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  insect_damage: {
    name: "Insect Damage",
    // displayName: "",
    description:
      "Indicates whether or not insect damage has been detected in the image.",
    // unit: "",
    value: (value) =>
      value === null ? null : value === "1" ? "Detected" : "Not Detected",
    svg: InsectSVG,
    additionalClassNames: (value) =>
      value === "1" ? "SVGFillRed" : "SVGFillGreen",
  },
  purple_leaves: {
    name: "Purple Leaves",
    // displayName: "",
    description: "The % of ground covered by crop with purple leaves.",
    unit: "%",
    svg: CropUnhealthySVG,
    additionalClassNames: "SVGFillRed",
  },
  ripening: {
    name: "Ripening",
    // displayName: "",
    description:
      "Ripening Ratio\nThis shows the percentage of ripening* in each image/field. This is shown by the change in colour from green to yellow throughout the pods.\n\n* BBCH Growth Stage 80-89",
    unit: "%",
    svg: FruitSVG,
    additionalClassNames: "SVGFillAmber",
  },
  senescence: {
    name: "Senescence",
    // displayName: "",
    description:
      "This aims to show the percentage of crop die-off or senescence*, i.e. soon to be ready for dessication, swathing and/or harvest. This is as the plants turn a dark brown and dry out.\n\n* BBCH Growth Stage 90-99",
    unit: "%",
    svg: FruitSVG,
    additionalClassNames: "SVGFillBlack",
  },
  unhealthy: {
    name: "Unhealthy",
    // displayName: "",
    description: "Unhealthy Crop\nThe % of ground covered by unhealthy crop.",
    unit: "%",
    svg: CropUnhealthySVG,
    additionalClassNames: "SVGFillRed",
  },
  weed_count: {
    name: "Weed Count",
    // displayName: "",
    description:
      "Weed counts are given as a simple number of weeds counted in the image, and also per area value; it is an average based on the area covered by an image and the total weeds in the image. A weed is classed as any plant that is not crop.",
    // unit: "",
    svg: CountSVG,
    additionalClassNames: "SVGFillRed",
  },
  weed_count_per_area: {
    name: "Weed Count per Area",
    // displayName: "",
    description:
      "Weed counts are given as a simple number of weeds counted in the image, and also per area value; it is an average based on the area covered by an image and the total weeds in the image. A weed is classed as any plant that is not crop.",
    unit: unitArea,
    svg: CountSVG,
    additionalClassNames: "SVGFillRed",
  },
  weeds: {
    name: "Weeds",
    // displayName: "",
    description:
      "Weeds\nThis is the % of ground covered by weeds. A weed is classed as any plant that is not crop.",
    unit: "%",
    svg: WeedsSVG,
    additionalClassNames: "SVGFillRed",
  },
  yellow_leaves: {
    name: "Yellow Leaves",
    // displayName: "",
    description: "The % of ground covered by crop with yellow leaves.",
    unit: "%",
    svg: CropUnhealthySVG,
    additionalClassNames: "SVGFillRed",
  },
};

/**
 * This contains the configurations for each POI stat
 */
export const POI_STATS: Record<keyof POIStats, StatConfig> = {
  hole_count_per_area: {
    name: "Hole Count per Area",
    // displayName: "",
    description:
      "Hole counts are given as a simple number of holes counted in the image, and also per area value; it is an average based on the area covered by an image and the total holes in the image.",
    unit: unitArea,
    svg: CountSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  poi_bugs: {
    name: "Insect Damage",
    // displayName: "",
    description:
      "Indicates whether or not insect damage has been detected in the image.",
    // unit: "",
    value: (value) =>
      value === null ? null : value === "1" ? "Detected" : "Not Detected",
    svg: InsectSVG,
    additionalClassNames: (value) =>
      value === "1" ? "SVGFillRed" : "SVGFillGreen",
  },
  poi_ear_count: {
    name: "Ear Count",
    // displayName: "",
    description:
      "Ear counts are given as a simple number of ears counted in the image, and also per area value; it is an average based on the area covered by an image and the total ears in the image.",
    // unit: "",
    svg: EarCountSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  poi_ear_count_per_area: {
    name: "Ear Count per Area",
    // displayName: "",
    description:
      "Ear counts are given as a simple number of ears counted in the image, and also per area value; it is an average based on the area covered by an image and the total ears in the image.",
    unit: unitArea,
    svg: EarCountSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  poi_count: {
    name: "Count",
    // displayName: "",
    description:
      "Counts are given as a simple number of plants counted in the image, and also a per square meter value, it is an average based on the area covered by an image and the total plants in the image.",
    // unit: "",
    svg: CountSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  poi_count_per_area: {
    name: "Count per Area",
    // displayName: "",
    description:
      "Counts are given as a simple number of plants counted in the image, and also a per square meter value, it is an average based on the area covered by an image and the total plants in the image.",
    unit: unitArea,
    svg: CountSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  poi_cover: {
    name: "Crop Cover",
    // displayName: "",
    description:
      "Crop Cover\nThis is the % of ground covered by healthy crop. 100% would mean that healthy crop covers the entire image.",
    unit: "%",
    svg: CropHealthySVG,
    // additionalClassNames: "SVGFillGreen",
  },
  poi_flowering: {
    name: "Flowering",
    // displayName: "",
    description:
      "Flowering Ratio (or Flower Fraction) is the percentage of the crop that is flowering vs not flowering. 100% would mean all of the crop is flowering. Note that the Crop Cover measurement includes green crop and flowering crop, the GAI measurement is green crop only.",
    unit: "%",
    svg: FloweringSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  poi_gai: {
    name: "GAI",
    // displayName: "",
    description:
      "Crop GAI\nGreen Area Index. This stat gives a value estimate of leaf cover. It is based on healthy crop only and can be used as an estimate for crop biomass.",
    // unit: "",
    svg: GaiSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  poi_growth_stage_aab: {
    name: "Growth Stage AAB",
    // displayName: "",
    description: "Growth Stage AAB",
    // unit: "",
    svg: GrowthStageSVG,
  },
  poi_growth_stage_bbch: {
    name: "Growth Stage BBCH",
    // displayName: "",
    description: "Growth Stage BBCH",
    // unit: "",
    svg: GrowthStageSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  poi_pod_development: {
    name: "Pod Development",
    // displayName: "",
    description:
      "This shows the percentage of each image/field that is going through seed/pod development*. In this case the plants will still be green and no flowers will remain.\n\n* BBCH Growth Stage 70-79",
    unit: "%",
    svg: FruitSVG,
    // additionalClassNames: "SVGFillGreen",
  },
  poi_ripening: {
    name: "Ripening",
    // displayName: "",
    description:
      "Ripening Ratio\nThis shows the percentage of ripening* in each image/field. This is shown by the change in colour from green to yellow throughout the pods.\n\n* BBCH Growth Stage 80-89",
    unit: "%",
    svg: FruitSVG,
    additionalClassNames: "SVGFillAmber",
  },
  poi_senescence: {
    name: "Senescence",
    // displayName: "",
    description:
      "This aims to show the percentage of crop die-off or senescence*, i.e. soon to be ready for dessication, swathing and/or harvest. This is as the plants turn a dark brown and dry out.\n\n* BBCH Growth Stage 90-99",
    unit: "%",
    svg: FruitSVG,
    additionalClassNames: "SVGFillBlack",
  },
  poi_unhealthy: {
    name: "Unhealthy",
    // displayName: "",
    description: "Unhealthy Crop\nThe % of ground covered by unhealthy crop.",
    unit: "%",
    svg: CropUnhealthySVG,
    additionalClassNames: "SVGFillRed",
  },
  poi_weed_count: {
    name: "Weed Count",
    // displayName: "",
    description:
      "Weed counts are given as a simple number of weeds counted in the image, and also per area value; it is an average based on the area covered by an image and the total weeds in the image. A weed is classed as any plant that is not crop.",
    // unit: "",
    svg: CountSVG,
    additionalClassNames: "SVGFillRed",
  },
  poi_weed_count_per_area: {
    name: "Weed Count per Area",
    // displayName: "",
    description:
      "Weed counts are given as a simple number of weeds counted in the image, and also per area value; it is an average based on the area covered by an image and the total weeds in the image. A weed is classed as any plant that is not crop.",
    unit: unitArea,
    svg: CountSVG,
    additionalClassNames: "SVGFillRed",
  },
  poi_weeds: {
    name: "Weeds",
    // displayName: "",
    description:
      "Weeds\nThis is the % of ground covered by weeds (plants that are not crop).",
    unit: "%",
    svg: WeedsSVG,
    additionalClassNames: "SVGFillRed",
  },
  purple_leaves: {
    name: "Purple Leaves",
    // displayName: "",
    description: "The % of ground covered by crop with purple leaves.",
    unit: "%",
    svg: CropUnhealthySVG,
    additionalClassNames: "SVGFillRed",
  },
  yellow_leaves: {
    name: "Yellow Leaves",
    // displayName: "",
    description: "The % of ground covered by crop with yellow leaves.",
    unit: "%",
    svg: CropUnhealthySVG,
    additionalClassNames: "SVGFillRed",
  },
  // POI SAT?
  // "Field NDVI\nThis stat is a measurement for plant health/vigor based on 10m square resolution satellite data. It gives a good general estimate for the amount of plant matter. Due to the resolution, it may not be accurate for specific points in the field and should be used only as a guide."
};
